/* eslint-disable camelcase */
import {Trans} from '@lingui/macro';
import React, {FC, useEffect} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {fontSizes} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {Link} from '../../../../../../components/common/links/link';
import {LinkTrap} from '../../../../../../components/common/linkTrap/linkTrap';
import {useLinkNavigation} from '../../../../../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {NicereplyIntegrationSettingsModel} from '../../../../../../models/api/integrationModel';
import {getCompanyUrl} from '../../../../../../store/data/company/companySelectors';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {maybeRenderConfigureDialogTextBox} from '../shared/settingsConfigureHelpers';

/*
 * Constants.
 */

const seeHowItWorksLink = 'http://help.frontapp.com/t/636k0d/how-to-enable-and-use-the-nicereply-integration';
const nicereplyCompanyUrlLink = 'https://admin.nicereply.com/settings/company';

/*
 * Props.
 */

interface SettingsAppsConfigureNicereplyProps {
  configurationSettings?: NicereplyIntegrationSettingsModel;
  onIsSaveEnabled: HandlerOf<boolean>;
  setConfigurationSettings: HandlerOf<NicereplyIntegrationSettingsModel | undefined>;
}

/*
 * Style.
 */

const StyledSeeHowItWorksWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
  margin-bottom: 16px;
`;

const StyledDisclaimerWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade60};
  margin-bottom: 16px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 8px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureNicereply: FC<SettingsAppsConfigureNicereplyProps> = (props) => {
  const {configurationSettings, onIsSaveEnabled, setConfigurationSettings} = props;
  const {navigateToLink} = useLinkNavigation();
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const companyUrl = useAppSelector(getCompanyUrl);

  // Controls if save is enabled for this dialog.
  //  This requires the api-key to be filled out, but there is no validation.
  useEffect(() => {
    if (!configurationSettings || !isGlobalAdmin) {
      onIsSaveEnabled(false);
      return;
    }
    const {org_name} = configurationSettings;
    onIsSaveEnabled(Boolean(org_name));
  }, [configurationSettings, isGlobalAdmin, onIsSaveEnabled]);

  const onDownloadCSVClick = () => {
    window.open(`${companyUrl}/integration_types/nicereply/team/csv`);
  };

  return (
    <>
      <StyledSeeHowItWorksWrapperDiv>
        <Button variant={ButtonVariantsEnum.LINK} onClick={() => navigateToLink(seeHowItWorksLink)}>
          <Trans id="settings-apps-configure-app-dialog-nicereply-see-how-it-works">See how it works</Trans>
        </Button>
      </StyledSeeHowItWorksWrapperDiv>
      {maybeRenderConfigureDialogTextBox(
        configurationSettings?.org_name || '',
        <Trans id="settings-apps-configure-app-dialog-nicereply-company-url">Company URL</Trans>,
        (value) => {
          setConfigurationSettings({
            ...configurationSettings,
            org_name: value.trim(),
          });
        },
        {
          isDisabled: !isGlobalAdmin,
          isRequired: true,
        },
      )}
      <LinkTrap>
        <StyledDisclaimerWrapperDiv>
          <Trans id="settings-apps-configure-app-dialog-nicereply-disclaimer">
            The company url is available in your
          </Trans>
          <Link to={nicereplyCompanyUrlLink} onClick={(event) => event.preventDefault()}>
            <Trans id="settings-apps-configure-app-dialog-nicereply-settings-link">Nicereply settings</Trans>
          </Link>
        </StyledDisclaimerWrapperDiv>
      </LinkTrap>
      <Trans id="settings-apps-configure-app-dialog-nicereply-export-csv-description">
        Use this file to quickly configure your team in Nicereply.
      </Trans>
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.PRIMARY} isDisabled={!isGlobalAdmin} onClick={onDownloadCSVClick}>
          <Trans id="settings-apps-configure-app-dialog-nicereply-export-csv-button">Export Team CSV</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
};
